const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:1337') + '/api';

interface FieldConfig {
  type: string;
  required: boolean;
  description: string;
  label: string;
  placeholder: string;
  helpText: string;
  visible: boolean;
  order: number;
  validation: any;
  options?: Array<{ label: string; value: string }>;
}

interface SchemaResponse {
  contentType: string;
  projectType: string;
  fields: Record<string, FieldConfig>;
  metadata: {
    displayName: string;
    description: string;
  };
}

interface Checklist {
  id: number;
  status: string;
  testEnvironmentDetails: string;
  accessCredentialsInfo: string;
  scopeConfirmation: boolean;
  additionalNotes: string;
  submittedDate: string;
  project?: {
    id: number;
    pid: string;
  };
}

interface Contact {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface Client {
  id: number;
  name: string;
  contactName?: string; // legacy
  contactFirstName?: string;
  contactLastName?: string;
  contactEmail?: string;
}

interface DistributionGroup {
  id: number;
  groupName: string;
  description: string;
  contacts: Contact[];
}

interface ScopeVersion {
  id: number;
  versionNumber: number;
  scopeContent: string;
  changeNotes?: string;
  versionStatus: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  createdAt: string;
  approvalDate?: string;
  rejectionReason?: string;
  acknowledgedByClient: boolean;
  acknowledgementDate?: string;
  createdByUser?: { id: number; username: string };
  approvedByUser?: { id: number; username: string };
  acknowledgedByUser?: { id: number; username: string };
}

interface ProjectType {
  id: number;
  documentId: string;
  name: string;
  zohoValue?: string;
  category?: string;
}

interface Methodology {
  id: number;
  documentId: string;
  title: string;
  methodologyDescription?: string;
  notes?: string;
}

interface Project {
  id: number;
  pid: string;
  client?: Client;
  projectTypes?: ProjectType[];
  customProjectType?: string;
  status?: string;
  projectStatus?: string;
  startDate?: string;
  endDate?: string;
  retestDate?: string;
  scope: string;
  pendingScopeAcknowledgement?: boolean;
  currentScopeVersion?: ScopeVersion;
  scopeVersions?: ScopeVersion[];
  distributionGroups?: DistributionGroup[];
  methodologies?: Methodology[];
}

interface ProjectsResponse {
  data: Project[];
}

interface ProjectResponse {
  data: Project;
}

interface MFASetupData {
  secret: string;
  qrCode: string;
  manualEntryKey: string;
}

class ApiService {
  private token: string | null = null;

  constructor() {
    // Initialize token from localStorage
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Authentication endpoints
  async login(username: string, password: string) {
    return this.request('/auth/local', {
      method: 'POST',
      body: JSON.stringify({
        identifier: username,
        password: password,
      }),
    });
  }

  async verifyMFA(token: string) {
    return this.request('/auth/mfa/login-verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async setupMFA(tempToken?: string): Promise<MFASetupData> {
    return this.request<MFASetupData>('/auth/mfa/setup', {
      method: 'POST',
      body: JSON.stringify({ tempToken }),
    });
  }

  async enableMFA(token: string, tempToken?: string): Promise<any> {
    return this.request('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ token, tempToken }),
    });
  }

  async getUserInfo() {
    return this.request('/users/me?populate=*');
  }

  async updateUser(data: any) {
    return this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Project endpoints
  async getProjects(): Promise<ProjectsResponse> {
    return this.request<ProjectsResponse>('/projects?populate=*');
  }

  async getProjectById(id: string): Promise<ProjectResponse> {
    // Explicitly populate relations to ensure all nested fields are returned
    const populateParams = 'populate[client]=*&populate[projectTypes]=*&populate[scopeVersions]=*&populate[clientUsers]=*&populate[methodology]=*';
    
    // Check if id is numeric (database ID) or string (PID)
    if (isNaN(Number(id))) {
      // It's a PID, search by PID
      const response = await this.request<ProjectsResponse>(`/projects?filters[pid][$eq]=${id}&${populateParams}`);
      if (response.data && response.data.length > 0) {
        return { data: response.data[0] };
      } else {
        throw new Error('Project not found');
      }
    } else {
      // It's a numeric ID
      return this.request<ProjectResponse>(`/projects/${id}?${populateParams}`);
    }
  }

  async getProjectByPid(pid: string): Promise<ProjectsResponse> {
    return this.request<ProjectsResponse>(`/projects?filters[pid][$eq]=${pid}&populate=*`);
  }

  // Schema endpoints
  async getContentTypeSchema(contentType: string, projectType: string): Promise<SchemaResponse> {
    return this.request<SchemaResponse>(`/schema/${contentType}/${projectType}`);
  }

  // Checklist endpoints
  async getOrCreateChecklist(pid: string): Promise<Checklist> {
    return this.request<Checklist>(`/requirement-checklists/project/${pid}`);
  }

  async submitChecklist(data: any) {
    return this.request('/requirement-checklists/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Distribution group endpoints
  async getProjectDistributionGroups(pid: string): Promise<DistributionGroup[]> {
    return this.request<DistributionGroup[]>(`/distribution-groups/project/${pid}`);
  }

  async getAllDistributionGroups(): Promise<{data: DistributionGroup[]}> {
    return this.request<{data: DistributionGroup[]}>('/distribution-groups?populate=*');
  }

  async getDistributionGroupsByClient(clientId: number): Promise<{data: DistributionGroup[]}> {
    return this.request<{data: DistributionGroup[]}>(`/distribution-groups?filters[client][id][$eq]=${clientId}&populate=*`);
  }

  async updateProjectDistributionGroups(pid: string, distributionGroupIds: number[]) {
    return this.request(`/distribution-groups/project/${pid}`, {
      method: 'PUT',
      body: JSON.stringify({ distributionGroupIds }),
    });
  }

  async createProjectDistributionGroup(pid: string, data: any) {
    return this.request(`/distribution-groups/project/${pid}/create`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDistributionGroup(id: number, data: any) {
    return this.request(`/distribution-groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ data }),
    });
  }

  // Form submission endpoints
  async submitFeedback(data: any) {
    return this.request('/client-feedbacks/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async submitRetest(data: any) {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  }

  async createNextcloudShare(projectId: number, recipientEmail: string, recipientName: string) {
    return this.request('/nextcloud/generate-upload-link', {
      method: 'POST',
      body: JSON.stringify({ projectId, recipientEmail, recipientName }),
    });
  }

  // Scope Version endpoints
  async getScopeHistory(projectId: number): Promise<ScopeVersion[]> {
    return this.request<ScopeVersion[]>(`/projects/${projectId}/scope-history`);
  }

  async createScopeDraft(projectId: number, scopeContent: string, changeNotes?: string): Promise<ScopeVersion> {
    return this.request<ScopeVersion>('/project-scope-versions/create-draft', {
      method: 'POST',
      body: JSON.stringify({ projectId, scopeContent, changeNotes }),
    });
  }

  async submitScopeForApproval(versionId: number): Promise<ScopeVersion> {
    return this.request<ScopeVersion>(`/project-scope-versions/${versionId}/submit`, {
      method: 'PUT',
    });
  }

  async approveScopeVersion(versionId: number): Promise<ScopeVersion> {
    return this.request<ScopeVersion>(`/project-scope-versions/${versionId}/approve`, {
      method: 'PUT',
    });
  }

  async rejectScopeVersion(versionId: number, rejectionReason: string): Promise<ScopeVersion> {
    return this.request<ScopeVersion>(`/project-scope-versions/${versionId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ rejectionReason }),
    });
  }

  async acknowledgeScopeVersion(versionId: number): Promise<ScopeVersion> {
    return this.request<ScopeVersion>(`/project-scope-versions/${versionId}/acknowledge`, {
      method: 'PUT',
    });
  }

  // Wizard Session endpoints
  async validateWizardToken(token: string): Promise<{ valid: boolean; error?: string; session?: any }> {
    return this.request<{ valid: boolean; error?: string; session?: any }>(
      `/wizard-sessions/validate/${token}`
    );
  }

  async saveWizardProgress(token: string, currentStep: number, wizardData: any): Promise<any> {
    return this.request(`/wizard-sessions/${token}/progress`, {
      method: 'PUT',
      body: JSON.stringify({ currentStep, wizardData }),
    });
  }

  async completeWizard(token: string, finalData: any): Promise<any> {
    return this.request(`/wizard-sessions/${token}/complete`, {
      method: 'PUT',
      body: JSON.stringify(finalData),
    });
  }

  async getProjectWizardSessions(projectId: number): Promise<{ data: WizardSession[] }> {
    return this.request<{ data: WizardSession[] }>(`/projects/${projectId}/wizard-sessions`);
  }

  async generateWizardLink(projectId: number, sendEmail: boolean = false): Promise<GenerateWizardLinkResponse> {
    return this.request<GenerateWizardLinkResponse>(`/projects/${projectId}/generate-wizard-link`, {
      method: 'POST',
      body: JSON.stringify({ sendEmail }),
    });
  }
}

interface WizardSession {
  id: number;
  wizardToken: string;
  wizardType: string;
  wizardStatus: 'pending' | 'in_progress' | 'completed' | 'expired';
  expiresAt: string;
  completedAt?: string;
  completedBy?: { id: number; username: string; email: string };
  currentStep: number;
  wizardLink?: string;
  createdAt: string;
}

interface GenerateWizardLinkResponse {
  success: boolean;
  wizardSession: {
    id: number;
    wizardToken: string;
    wizardStatus: string;
    expiresAt: string;
  };
  wizardLink: string;
  emailsSent: boolean;
}

export type { ScopeVersion, Project, DistributionGroup, Contact, Client, WizardSession, GenerateWizardLinkResponse };

export const apiService = new ApiService();
